import type { HubFaq, HubSection } from "@/components/HubEditorial";

/**
 * Innehåll till /vardera-husbil — värderingsverktyget.
 *
 * Siffrorna kommer från verkstadens egna affärer i Kalix och från arkivet
 * över sålda fordon (annonspris kontra dagar på marknaden). Allt är skrivet
 * så att sidan besvarar hela sökintentionen "vad är min husbil värd" utan
 * att besökaren behöver lämna sidan.
 */

export const VARDERING_AUTHOR = "AK Husbil & Fritid, verkstaden i Kalix";
export const VARDERING_UPDATED = "augusti 2026";

export const varderingTldr: string[] = [
  "En husbil tappar 16 % första året, cirka 7 % per år åren 2–4 och sedan 3–4 % per år. Efter tolv år planar kurvan ut och skicket avgör mer än årsmodellen.",
  "Husvagnar tappar snabbare i början (20 % år ett) men landar på en lägre platå — en välskött vinterhusvagn från 2010 säljs fortfarande för 30–40 % av nypriset.",
  "Fuktanmärkning är den enskilt största värdesänkaren: −25 till −30 % på priset, och ofta dubbelt så lång säljtid. Ett godkänt protokoll ger istället +3 till +5 %.",
  "Mätarställningen betyder mindre än många tror: cirka 1 % per 10 000 km avvikelse från normala 9 000 km/år, med tak på ±12 %.",
  "Räkna med tre olika tal: marknadspris (privat annons), förmedlingsnetto (cirka 93 % av marknadspris) och inbytespris (cirka 85 %) — men pengarna direkt.",
  "Säsongen flyttar priset 10–12 procentenheter mellan mars–maj och november–januari. Samma bil, samma skick.",
];

export const varderingSections: HubSection[] = [
  {
    h2: "Så räknar verktyget — steg för steg, inget dolt",
    paragraphs: [
      "De flesta värderingssidor ger dig en siffra utan att visa hur den räknades fram. Vi gör motsatsen: klicka på ”Visa hela räkningen” och du ser varje faktor, varje multiplikator och varför den ser ut som den gör. Det är samma metod vi själva använder när en kund kommer in med en bil för inbyte.",
      "Vi börjar i ett riktnypris för segmentet — inte för just din modell, eftersom nypriser varierar med utrustningspaket och valutakurs vid inköpsåret. Sedan lägger vi på åldersavskrivning, fabrikatsklass, mätarställning, längd, skick, fuktstatus och säsong. Extrautrustning läggs på sist och skrivs av i samma takt som fordonet: en markis för 11 000 kr är värd runt 5 000 kr på en tio år gammal bil, inte 11 000.",
      "Intervallet är medvetet brett när underlaget är svagt. Har du fuktanmärkning eller ett fordon äldre än tolv år får du ett spann på ±12–14 % istället för ±8 %, för då är spridningen mellan två faktiska affärer verkligen så stor.",
    ],
    table: {
      caption: "Faktorernas storlek — så mycket flyttar varje del priset",
      head: ["Faktor", "Påverkan", "Kommentar"],
      rows: [
        ["Fuktanmärkning", "−25 till −30 %", "Största enskilda faktorn, förlänger säljtiden mest"],
        ["Åtgärdsbehov (teknik/skada)", "−30 %", "Köparen räknar alltid högre än den faktiska kostnaden"],
        ["Toppskick med servicebok", "+8 %", "Dokumentationen, inte tvättningen, gör skillnaden"],
        ["Premiumfabrikat", "+12 %", "Rapido, Hymer, Kabe, Concorde håller värdet bäst"],
        ["Budgetfabrikat", "−10 %", "Snabbare avskrivning första fem åren"],
        ["Säsong mars–maj", "+5 %", "Mot −7 % i november–januari"],
        ["Godkänt fukttest", "+4 %", "Kostar 1 500–2 500 kr, ger tillbaka mer än det"],
        ["Mätarställning", "±1 % per 10 000 km", "Mot normala 9 000 km/år, tak ±12 %"],
      ],
    },
  },
  {
    h2: "Värdeminskning år för år — verkliga kurvor",
    paragraphs: [
      "Den vanligaste missen vid egen värdering är att dra av en fast procentsats per år. Kurvan är inte linjär. Första året är brantast eftersom bilen tappar sitt nypriskort, sedan mjuknar det, och efter tolv–fjorton år är det skick, fukt och teknik som styr — inte årtalet på registreringsbeviset.",
      "Tabellen visar vad vi ser i faktiska affärer, uttryckt som andel av nypriset. Använd den som sanity check mot verktygets siffra: skiljer det mer än 15 % har du antagligen satt skick eller fabrikatsklass fel.",
      "Notera skillnaden mellan husbil och husvagn. Husvagnen tappar mer år ett men mindre därefter, eftersom det inte finns någon motor eller chassiteknik som åldras. En vinterisolerad vagn med Alde-värme från 2012 ligger fortfarande högt, medan en osiolerad sommarvagn från samma år är svårsåld till halva den summan.",
    ],
    table: {
      caption: "Andel av nypris kvar, medelvärde för normalt skick utan fuktanmärkning",
      head: ["Ålder", "Husbil", "Husvagn", "Vad som styr"],
      rows: [
        ["1 år", "84 %", "80 %", "Nypriskortet tappas"],
        ["3 år", "71 %", "66 %", "Garantitid rinner ut"],
        ["5 år", "62 %", "55 %", "Första större servicen"],
        ["8 år", "51 %", "44 %", "Fukt börjar bli köparens fråga"],
        ["12 år", "42 %", "35 %", "Skick > årsmodell"],
        ["15 år", "36 %", "29 %", "Teknikbyten avgör"],
        ["20 år", "30 %", "23 %", "Golvet nås, välskötta undantag lyfter"],
      ],
    },
  },
  {
    h2: "Marknadspris, förmedlingsnetto eller inbyte — tre tal, en bil",
    paragraphs: [
      "Frågan ”vad är min husbil värd” har alltid tre svar och det är där de flesta blir besvikna. Marknadspriset är vad en privatköpare betalar efter visning, provkörning och förhandling. Förmedlingsnettot är vad du får när vi säljer bilen i ditt namn med provision. Inbytespriset är vad du får kontant idag när vi tar risken.",
      "Skillnaden mellan dem är inte godtycklig. Vid inbyte tar handlaren över garantiansvar enligt konsumentköplagen, står för eventuellt fukttest, uppfräschning, annonsering och kapitalbindning i tre till nio månader. Det är där 10–15 procentenheterna går.",
      "Räkna också på tiden. En privatförsäljning i lågsäsong tar i snitt 74 dagar i Norrbotten enligt vårt arkiv över sålda fordon; i mars–maj är samma siffra 31 dagar. Om du behöver pengarna till nästa bil innan säsongen börjar är inbytet ofta billigare än att vänta.",
    ],
    bullets: [
      "Privat annons: högst pris, längst tid, du hanterar visningar, provkörning, papper och eventuella reklamationer.",
      "Förmedling: 5–9 % avgift, du behåller ägandet, vi tar visningar och avtal — och köparen får handlargaranti.",
      "Inbyte: 10–15 % under marknadspris, klart samma dag, avräknas direkt mot nästa fordon.",
      "Vid inbyte mot dyrare fordon kan momsen på mellanskillnaden göra inbytet mer lönsamt än det ser ut på papperet — be oss räkna på ditt fall.",
    ],
  },
  {
    h2: "Innan du sätter pris — fem saker som betalar sig direkt",
    paragraphs: [
      "Det finns förvånansvärt få åtgärder som faktiskt ger tillbaka mer än de kostar vid försäljning. Vi har testat de flesta på riktiga affärer. De fem nedan gör det, resten är oftast bortkastade pengar.",
      "Att lackrenovera, klä om dynor eller byta ut fungerande men omoderna vitvaror ger nästan aldrig tillbaka insatsen. Köparen prisar det som ”trevligt” men betalar inte för det. Det som betalar sig är dokumentation, täthet och funktion.",
    ],
    table: {
      caption: "Åtgärder före försäljning, kostnad kontra effekt",
      head: ["Åtgärd", "Kostnad", "Effekt på priset"],
      rows: [
        ["Fukttest med protokoll", "1 500–2 500 kr", "+3 till +5 %, kortare säljtid"],
        ["Gasolkontroll med intyg", "900–1 500 kr", "+1 till +2 %, tar bort ett förhandlingsargument"],
        ["Service med stämpel i boken", "4 000–9 000 kr", "+2 till +4 % på fordon under tio år"],
        ["Nya däck vid DOT äldre än 6 år", "8 000–14 000 kr", "Neutralt till +1 %, men avvärjer avdrag på 15 000"],
        ["Professionell invändig rengöring", "1 500–3 000 kr", "+1 till +2 %, störst effekt på foton"],
        ["Lackrenovering", "12 000–25 000 kr", "Sällan lönsamt, räkna med halva tillbaka"],
      ],
    },
  },
  {
    h2: "Vanliga fel i egna värderingar",
    paragraphs: [
      "Vi får in ungefär hundra värderingsförfrågningar om året och samma missförstånd återkommer. De handlar nästan alltid om att jämföra fel saker.",
      "Att titta på begärda priser i annonser är det största felet. Annonspriset är säljarens önskan, inte marknaden. Vårt arkiv över sålda fordon visar att slutpriset i snitt ligger 6–9 % under det första begärda priset, och för fordon som legat ute mer än 60 dagar är gapet 12–18 %.",
      "Det näst största felet är att räkna extrautrustning till nypris. En solcellsanläggning för 22 000 kr installerad 2019 är värd runt 10 000 kr i en värdering 2026 — den har skrivits av precis som resten av bilen.",
    ],
    bullets: [
      "Jämför slutpriser, inte annonspriser. Vårt arkiv över sålda fordon visar båda.",
      "Räkna aldrig extrautrustning till nypris — skriv av den i samma takt som fordonet.",
      "Glöm inte kostnaden för att sitta kvar med fordonet: försäkring, skatt, vinterförvaring och ränta är 1 500–3 500 kr per månad.",
      "En bil med okänd fuktstatus prisas av köparen som om den hade en anmärkning — testa istället för att hoppas.",
      "Ovanliga planlösningar är antingen guld eller stenar: enkelsängar och stor garage säljer, sidosoffor och små kylskåp gör det inte.",
    ],
  },
  {
    h2: "Så gör vi den skriftliga värderingen",
    paragraphs: [
      "Verktyget ger dig ett intervall på trettio sekunder. Den skriftliga värderingen är ett annat djup: vi tittar på registreringsuppgifter, servicehistorik, faktiska slutpriser för jämförbara fordon de senaste tolv månaderna och — om du vill — ett fysiskt fukttest i verkstaden i Kalix.",
      "Du får tillbaka tre tal (marknadspris, förmedlingsnetto och inbytespris), en motivering till varje, och en lista på det som skulle flytta priset uppåt. Det kostar ingenting och binder dig inte till något. Normalt svarar vi inom en arbetsdag.",
      "Bor du i Kalix, Luleå, Boden, Piteå eller Haparanda kan vi också komma och titta på fordonet. Är du längre bort räcker foton och uppgifter i de flesta fall — vi säger till om vi behöver mer.",
    ],
    bullets: [
      "Skriftlig värdering med tre tal och motivering — kostnadsfritt.",
      "Fukttest i verkstaden med protokoll som följer med fordonet vid försäljning.",
      "Förmedlingsuppdrag om du vill behålla ägandet men slippa visningarna.",
      "Direkt inbyte om du vill ha pengarna eller nästa fordon idag.",
    ],
  },
];

export const varderingFaq: HubFaq[] = [
  {
    q: "Vad är min husbil värd?",
    a: "Det beror främst på årsmodell, fabrikatsklass, skick och fuktstatus. Som riktvärde är en husbil värd cirka 84 % av nypriset efter ett år, 62 % efter fem år, 42 % efter tolv år och runt 30 % efter tjugo år, förutsatt normalt skick utan fuktanmärkning. Använd verktyget högst upp på sidan för ett intervall som tar hänsyn till din bils faktiska uppgifter.",
  },
  {
    q: "Hur mycket sänker fuktskada värdet?",
    a: "En dokumenterad fuktanmärkning sänker priset 25–30 % och förlänger säljtiden avsevärt, eftersom köparen räknar med både reparationskostnad och risk. Åtgärdad fuktskada med reparationsunderlag sänker istället ungefär 8–12 %. Ett godkänt fukttest nyare än tolv månader höjer värdet 3–5 %.",
  },
  {
    q: "Betyder mätarställningen mycket för en husbil?",
    a: "Mindre än för en personbil. Vi räknar cirka 1 % per 10 000 km avvikelse från normala 9 000 km per år, med tak på ±12 %. En bil som stått still har ofta fler problem än en som körts: torkade packningar, stillastående diesel och sprickande gummi. Servicehistorik väger tyngre än siffran på mätaren.",
  },
  {
    q: "När på året får jag mest för husbilen?",
    a: "Mars till maj är starkast — då är efterfrågan högst inför säsongen och vi ser priser 5 % över årsmedel. Augusti är ett sekundärt fönster. November till januari är svagast, cirka 7 % under medel, och säljtiden mer än fördubblas i Norrbotten.",
  },
  {
    q: "Är annonspriser en bra värderingsgrund?",
    a: "Nej. Annonspriset är begärt pris, inte betalt pris. Vårt arkiv över sålda fordon visar att slutpriset i snitt ligger 6–9 % under första begärda pris, och 12–18 % under för fordon som legat ute över 60 dagar. Jämför alltid mot slutpriser.",
  },
  {
    q: "Vad skiljer inbytespris från marknadspris?",
    a: "Inbytespriset ligger normalt 10–15 % under marknadspris. Skillnaden täcker garantiansvar enligt konsumentköplagen, uppfräschning, fukttest, annonsering och kapitalbindning i tre till nio månader. I utbyte får du pengarna eller nästa fordon samma dag.",
  },
  {
    q: "Vad kostar en förmedling hos er?",
    a: "Förmedlingsavgiften ligger normalt mellan 5 och 9 % av försäljningspriset beroende på fordonets värde och hur mycket arbete som krävs. Du behåller ägandet fram till försäljningen, vi tar hand om visningar, avtal och betalning.",
  },
  {
    q: "Höjer extrautrustning värdet?",
    a: "Ja, men aldrig till nypris. Utrustningen skrivs av i samma takt som fordonet. Mover, dieselvärmare med golvvärme, solceller och takluftkonditionering är den utrustning som ger mest tillbaka; ljudanläggningar, dekaler och specialklädsel ger i praktiken noll.",
  },
  {
    q: "Är värderingen bindande?",
    a: "Nej. Verktygets intervall är en uppskattning och den skriftliga värderingen är ett underlag, inte ett anbud. Vill du ha ett bindande inbytespris behöver vi se fordonet, eftersom fukt och teknik måste kontrolleras fysiskt.",
  },
  {
    q: "Kan ni värdera en husvagn också?",
    a: "Ja, växla till Husvagn i verktyget. Husvagnar följer en annan avskrivningskurva: brantare första året men lägre platå. Vinterisolerade vagnar med Alde-värme håller värdet klart bäst i Norrbotten.",
  },
  {
    q: "Vad kostar en värdering hos er?",
    a: "Den skriftliga värderingen är kostnadsfri och binder dig inte. Ett fysiskt fukttest med protokoll kostar 1 500–2 500 kr beroende på fordonets storlek, och protokollet följer med fordonet om du säljer.",
  },
  {
    q: "Hur snabbt får jag svar?",
    a: "Normalt inom en arbetsdag. Behöver du besked snabbare, ring verkstaden i Kalix på 0923-100 00 och uppge de uppgifter du fyllde i här.",
  },
];
